// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    return {
        isAuthenticated: getSession(state).isAuthenticated,
        fulfillmentMethod: state.components.addressDrawer.fulfillmentMethod,
        isPunchOutSession: getIsPunchOutSession(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasShellContext;

interface AddressDrawerContainerStyles {
    drawerContainer?: GridContainerProps;
    punchOutAddressGridItem?: GridItemProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    addressesGridItemAnonymousUser?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    pickUpAddressGridItemAnonymousUser?: GridItemProps;
    applyButtonGridItem?: InjectableCss;
}

export const addressDrawerContainerStyles: AddressDrawerContainerStyles = {
    drawerContainer: {
        gap: 0,
    },
    punchOutAddressGridItem: {
        width: 12,
        css: css`
            padding: 15px 0;
        `,
    },
    fulfillmentMethodGridItem: {
        css: css`
            order: 1;
            padding: 15px 0;
        `,
        width: 12,
    },
    addressesGridItem: {
        css: css`
            order: 2;
            padding: 15px 10px 0 0;
            @media (max-width: 481px) {
                padding-right: 0;
            }
        `,
        width: [12, 6, 6, 6, 6],
    },
    addressesGridItemAnonymousUser: {
        css: css`
            order: 3;
            padding: 15px 0 0 10px;
            @media (max-width: 481px) {
                padding-left: 0;
            }
        `,
        width: [12, 6, 6, 6, 6],
    },
    pickUpAddressGridItem: {
        css: css`
            order: 3;
            padding: 15px 0 0 10px;
            @media (max-width: 481px) {
                padding-left: 0;
            }
        `,
        width: [12, 6, 6, 6, 6],
    },
    pickUpAddressGridItemAnonymousUser: {
        css: css`
            order: 2;
            padding: 15px 10px 0 0;
            @media (max-width: 481px) {
                padding-right: 0;
            }
        `,
        width: [12, 6, 6, 6, 6],
    },
    applyButtonGridItem: {
        css: css`
            order: 4;
            width: 100%;
        `,
    },
};

const styles = addressDrawerContainerStyles;

const AddressDrawerContainer = ({ id, isAuthenticated, fulfillmentMethod, isPunchOutSession, shellContext }: Props) => {
    return (
        <>
            {(isPunchOutSession || shellContext.isEditing) && (
                <GridContainer {...styles.drawerContainer}>
                    <GridItem {...styles.punchOutAddressGridItem}>
                        <Zone zoneName="PunchOutAddress" contentId={id} />
                    </GridItem>
                </GridContainer>
            )}
            {(!isPunchOutSession || shellContext.isEditing) && (
                <GridContainer {...styles.drawerContainer}>
                    <GridItem {...styles.fulfillmentMethodGridItem}>
                        <Zone zoneName="FulfillmentMethod" contentId={id} />
                    </GridItem>
                    <GridItem
                        {...(!isAuthenticated && fulfillmentMethod === FulfillmentMethod.PickUp
                            ? styles.addressesGridItemAnonymousUser
                            : styles.addressesGridItem)}
                    >
                        <Zone zoneName="Addresses" contentId={id} />
                    </GridItem>
                    <GridItem
                        {...(!isAuthenticated && fulfillmentMethod === FulfillmentMethod.PickUp
                            ? styles.pickUpAddressGridItemAnonymousUser
                            : styles.pickUpAddressGridItem)}
                    >
                        <Zone zoneName="PickUpAddress" contentId={id} />
                    </GridItem>
                    <StyledWrapper {...styles.applyButtonGridItem} className="modal-actions">
                        <Zone zoneName="ApplyButton" contentId={id} />
                    </StyledWrapper>
                </GridContainer>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(AddressDrawerContainer)),
    definition: {
        group: "Header",
        icon: "house",
    },
};

export default widgetModule;
